import { gql } from '@apollo/client';

/* ── the stock master ───────────────────────────────────────── */

/**
 * Valuation and quality fundamentals for a set of stocks, in one call.
 *
 * A fund's look-through holdings carry NSE symbols (`ICICIBANK.NSE`) that key
 * straight into this master, so the fund's own valuation is read by asking for
 * its holdings. Non-equity lines — repo, cash, government securities — carry
 * an internal id instead and simply do not resolve, which is correct.
 *
 * Only the valuation block is selected. The type also carries technicals,
 * financial statements, earnings and estimates, none of which this app reads.
 *
 * Ratios come back as plain multiples; `dividendYield`, `returnOnEquityTtm`
 * and `pePercentile` are percentages. `peSignal` grades the current multiple
 * against the stock's own history: cheap_vs_history, fair_vs_history,
 * expensive_vs_history.
 */
/**
 * Stock symbols to names, and nothing else.
 *
 * STOCKS_BY_SYMBOLS below carries each stock's whole fundamentals block, which
 * is the right call for a table of a fund's forty holdings and the wrong one
 * for naming a look-through: a portfolio's routes end in a thousand stocks,
 * and a name that waits on a thousand fundamentals records arrives late or not
 * at all — which is a table of symbols.
 */
export const STOCK_NAMES_BY_SYMBOLS = gql`
  query StockNamesBySymbols($securitySymbols: [String!]!) {
    getStocksBySymbols(securitySymbols: $securitySymbols) {
      securitySymbol
      name
    }
  }
`;

/** Names and sectors only — what a ranked list of stocks labels its rows with. */
export const STOCK_LABELS_BY_SYMBOLS = gql`
  query StockLabelsBySymbols($securitySymbols: [String!]!) {
    getStocksBySymbols(securitySymbols: $securitySymbols) {
      securitySymbol
      name
      sector
    }
  }
`;

export const STOCKS_BY_SYMBOLS = gql`
  query StocksBySymbols($securitySymbols: [String!]!) {
    getStocksBySymbols(securitySymbols: $securitySymbols) {
      securitySymbol
      name
      sector
      fundamentals {
        asOfDate
        marketCap
        # Risk metrics — what a holding contributes to a fund's realized
        # characteristics is its weight against its own volatility and beta.
        beta
        volatilityAnnual
        volatility30d
        sharpeRatio
        maxDrawdown
        peRatio
        forwardPe
        pegRatio
        priceBookMrq
        priceSalesTtm
        enterpriseValueEbitda
        dividendYield
        returnOnEquityTtm
        peZScore
        pePercentile
        peSignal
      }
    }
  }
`;

/**
 * The master, paged — the browse list behind the Stocks screen.
 *
 * `getStocks` has no search argument: the filters it takes are country,
 * exchange, sector and language, and a symbol lookup goes through
 * `getStockDetails` instead. The screen therefore switches queries rather than
 * narrowing this one, which is why the row shape below is a strict subset of
 * the detail selection.
 *
 * Every numeric arrives as a decimal string, not a number.
 */
/**
 * The same list, symbol and name only — for selecting every stock across all
 * pages, which pages through `getStocks` in large chunks.
 */
export const GET_STOCK_SYMBOLS = gql`
  query GetStockSymbols($skip: Int, $limit: Int, $exchange: String, $sector: String) {
    getStocks(skip: $skip, limit: $limit, exchange: $exchange, sector: $sector) {
      securitySymbol
      name
    }
  }
`;

export const GET_STOCKS = gql`
  query GetStocks(
    $skip: Int
    $limit: Int
    $country: String
    $exchange: String
    $sector: String
  ) {
    getStocks(
      skip: $skip
      limit: $limit
      country: $country
      exchange: $exchange
      sector: $sector
    ) {
      id
      securitySymbol
      name
      exchange
      currency
      country
      sector
      industry
      snapshot { asOfDate price changeP change volume prevClose }
      fundamentals { marketCap peRatio dividendYield return1y }
    }
  }
`;

/**
 * The browse row (`GET_STOCKS`'s shape) for given symbols — what the Stocks
 * table shows for the matches of a name/symbol search.
 */
export const STOCK_ROWS_BY_SYMBOLS = gql`
  query StockRowsBySymbols($securitySymbols: [String!]!) {
    getStocksBySymbols(securitySymbols: $securitySymbols) {
      id
      securitySymbol
      name
      exchange
      currency
      country
      sector
      industry
      snapshot { asOfDate price changeP change volume prevClose }
      fundamentals { marketCap peRatio dividendYield return1y }
    }
  }
`;

/**
 * A stock's latest research note from the stock research agent — its rating,
 * target and the two PDFs (`reportUrl`, and the `reportCseUrl` edition).
 */
export const STOCK_RESEARCH_NOTE = gql`
  query LatestStockReport($securitySymbol: String!) {
    getStockResearchNote(securitySymbol: $securitySymbol) {
      securitySymbol
      reviewId
      window
      asOf
      rating
      targetPrice
      upsidePct
      reportUrl
      reportCseUrl
      criticStatus
    }
  }
`;

/** Every research note written for a stock, newest first, with each one's PDFs. */
export const STOCK_RESEARCH_HISTORY = gql`
  query StockReportHistory($securitySymbol: String!, $limit: Int = 24) {
    getStockResearchHistory(securitySymbol: $securitySymbol, limit: $limit) {
      reviewId
      window
      createdAt
      generatedAt
      asOf
      isLatest
      headline
      criticStatus
      pdfUrl
      pdfDownloadUrl
      pdfCseUrl
      pdfCseDownloadUrl
      pdfGeneratedAt
    }
  }
`;

/**
 * One stock's whole record.
 *
 * Deliberately the full type rather than a narrowed selection: this screen is
 * the master's own detail view, and a field the master carries but the screen
 * hides is a field nobody can check. Ratios are plain multiples; `dividendYield`,
 * the margins, the returns and every `*Pct` percentile are percentages.
 *
 * `peerComparison`, `similarStocks` and `factorExposures` are populated by
 * separate backfill jobs and come back null or empty for most symbols, which is
 * a state to render rather than an error.
 */
export const GET_STOCK_DETAILS = gql`
  query GetStockDetails($securitySymbol: String!) {
    getStockDetails(securitySymbol: $securitySymbol) {
      id
      securitySymbol
      name
      exchange
      currency
      country
      stockType
      securityType
      isin
      cusip

      sector
      industry
      gicsSector
      gicsGroup
      gicsIndustry
      gicsSubIndustry

      employees
      description
      website

      snapshot {
        securitySymbol asOfDate price changeP volume
        open high low prevClose change
      }

      fundamentals {
        securitySymbol
        asOfDate
        return1d return1w return1m return3m return6m returnYtd
        return1y return3y return5y return10y
        marketCap enterpriseValue
        peRatio forwardPe trailingPe pegRatio
        priceSalesTtm priceBookMrq
        enterpriseValueRevenue enterpriseValueEbitda
        bookValue ebitda
        profitMargin operatingMarginTtm returnOnAssetsTtm returnOnEquityTtm
        grossMargin netMargin
        revenueTtm revenuePerShareTtm grossProfitTtm
        dilutedEpsTtm earningsShare
        epsEstimateCurrentYear epsEstimateNextYear epsGrowthEstimate
        dividendShare dividendYield dividendDate exDividendDate payoutRatio
        sharesOutstanding sharesFloat percentInsiders percentInstitutions
        sharesShort shortRatio
        beta week52High week52Low day50Ma day200Ma
        volatilityAnnual volatility30d sharpeRatio maxDrawdown
        debtToEquity interestCoverage currentRatio quickRatio
        assetTurnover fcfMargin priceToFcf
        rsi14d macdValue macdSignal macdHistogram
        peZScore pePercentile peSignal
        revenueGrowth1y epsGrowth1y
        revenueCagr3y revenueCagr5y epsCagr3y epsCagr5y
        netMarginTrend opMarginTrend roeQuality roeTrend debtTrend
        earningsBeatRate
        revenueCagr3ySectorPct revenueCagr3yUniversePct
        epsCagr3ySectorPct epsCagr3yUniversePct
        roeSectorPct roeUniversePct
        netMarginSectorPct netMarginUniversePct
        opMarginSectorPct opMarginUniversePct
        return1ySectorPct return1yUniversePct
        return3ySectorPct return3yUniversePct
        revenueConsistencyScore marginConsistencyScore
        roeConsistencyScore earningsConsistencyScore
      }

      financialStatements {
        securitySymbol filingDate periodEndDate fiscalPeriod fiscalYear currency
        epsBasic epsDiluted bookValuePerShare
        incomeStatement {
          totalRevenue costOfRevenue grossProfit operatingIncome netIncome ebitda ebit
        }
        balanceSheet {
          totalAssets totalLiabilities totalEquity currentAssets currentLiabilities
          cashAndEquivalents totalDebt netDebt
        }
        cashFlow {
          operatingCashFlow investingCashFlow financingCashFlow
          freeCashFlow capitalExpenditures dividendsPaid
        }
      }

      earnings {
        securitySymbol reportDate fiscalPeriod fiscalYear
        reportedEps estimatedEps surpriseEps surprisePercent
        reportedRevenue estimatedRevenue
      }

      estimates {
        securitySymbol estimatePeriod asOfDate numberOfAnalysts
        epsEstimateAvg epsEstimateLow epsEstimateHigh
        revenueEstimateAvg revenueEstimateLow revenueEstimateHigh
      }

      factorExposures {
        securitySymbol asOfDate
        alphaScore rSquared beta
        momentumScore valueScore qualityScore sizeScore growthScore
      }

      peerComparison {
        peerCount sector industry
        stock {
          securitySymbol name marketCap peRatio forwardPe priceBookMrq priceSalesTtm
          evEbitda dividendYield returnOnEquityTtm profitMargin
          revenueGrowth1y epsGrowth1y debtToEquity beta return1y return3y
        }
        peers {
          securitySymbol name marketCap peRatio forwardPe priceBookMrq priceSalesTtm
          evEbitda dividendYield returnOnEquityTtm profitMargin
          revenueGrowth1y epsGrowth1y debtToEquity beta return1y return3y
        }
      }

      similarStocks {
        securitySymbol name similarity sector industry marketCap peRatio
      }

      diversifyingStocks {
        securitySymbol name correlation sector industry marketCap
      }
    }
  }
`;

/**
 * Generated research reports for the stock master.
 *
 * One row per (symbol, archetype) unless `includeHistory` is set, in which case
 * every superseded generation comes back too. `reportUrl` is an external link
 * the screen opens rather than renders. The register is empty until something
 * has been generated, so an empty list is the normal first state.
 */
export const STOCK_RESEARCH_REPORTS = gql`
  query StockResearchReports(
    $securitySymbol: String
    $limit: Int
    $offset: Int
    $includeHistory: Boolean
  ) {
    getAllStockResearchReports(
      securitySymbol: $securitySymbol
      limit: $limit
      offset: $offset
      includeHistory: $includeHistory
    ) {
      id
      securitySymbol
      archetype
      reportUrl
      verified
      generatedAt
    }
  }
`;

/**
 * Name search over the security master, for the typeahead on the stock screen.
 *
 * `getStocks` has no search argument at all — it filters by exchange and
 * sector — and `getStockDetails` wants the exact symbol, so until this the
 * stock screen could only be searched by someone who already knew the ticker.
 * Typing "reliance" returned nothing.
 *
 * The resolver matches on name and symbol and falls back to a fuzzy score when
 * nothing matches outright, which is why `similarity` is selected: an exact hit
 * comes back 1.0 and a guess at a misspelling much lower, and the screen says
 * which it is showing rather than presenting the two alike.
 *
 * `entityType` is LOWER CASE — 'stock' and 'mf', the values the resolver also
 * returns in `type`. 'STOCK' is accepted by the schema and matches nothing.
 */
export const SEARCH_SECURITIES = gql`
  query SearchSecurities($query: String!, $limit: Int = 8, $entityType: String, $isEtf: Boolean) {
    searchSecurities(query: $query, limit: $limit, entityType: $entityType, isEtf: $isEtf) {
      symbol
      name
      type
      sector
      category
      fundHouse
      similarity
    }
  }
`;

/**
 * The STOCK-only search — what every stock box asks (the Stocks screen, the
 * desk's search, the stock pickers). `searchSecurities` stays for the boxes
 * that look across stocks and funds together.
 *
 * Narrowed by sector, industry, ETF-or-not and country; `minSimilarity` is
 * the floor below which a fuzzy guess is not returned at all, and
 * `similarity` still says how close each hit is (1.0 an outright match).
 */
export const SEARCH_STOCK_SECURITIES = gql`
  query SearchStockSecurities(
    $query: String!
    $limit: Int = 10
    $sector: String
    $industry: String
    $isEtf: Boolean
    $country: String
    $minSimilarity: Float = 0.3
  ) {
    searchStockSecurities(
      query: $query
      limit: $limit
      sector: $sector
      industry: $industry
      isEtf: $isEtf
      country: $country
      minSimilarity: $minSimilarity
    ) {
      symbol
      name
      fullName
      type
      similarity
      sector
      industry
      category
      bondType
      riskLevel
      isEtf
      country
      investmentMode
      assetClass
      marketCap
      peRatio
      fundHouse
      amcLogo
      fundStructure
      amfiCode
      schemeCode
      description
    }
  }
`;
